import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootContactCardProps, TootContactMethodType } from "./TootContactCard.props";

const typeLabels: Record<TootContactMethodType, string> = {
  email: "Email",
  general: "General",
  phone: "Phone",
  sales: "Sales",
  support: "Support",
};

export const TootContactCard = ({
  actionLabel,
  ariaLabel,
  className,
  description,
  href,
  label,
  prominence = "standard",
  target,
  type,
  value,
}: ITootContactCardProps) => {
  const Link = resolveTootPrimitive("Link");
  const isPrimary = prominence === "primary";
  const typeLabel = typeLabels[type];

  const body = (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.06)_1px,transparent_1px)] bg-[size:1.55rem_1.55rem] opacity-35 [mask-image:linear-gradient(180deg,black,transparent_96%)]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-4 top-4 h-2.5 w-2.5 rounded-full border border-[color-mix(in_oklab,var(--color-toot-orange)_70%,transparent)] bg-[var(--color-toot-red)] shadow-[0_0_1.2rem_rgba(255,77,46,0.72)]"
      />

      <div className="relative flex h-full min-w-0 flex-col p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 pr-7">
          <div className="min-w-0">
            <p className="m-0 text-[0.68rem] font-black uppercase leading-none tracking-[0.2em] text-[var(--color-toot-orange)]">
              {typeLabel}
            </p>
            <h3
              className={cn(
                "mt-3 text-balance font-black leading-[1.02] tracking-[-0.045em] text-[var(--color-toot-foreground)]",
                isPrimary ? "text-2xl md:text-[1.7rem]" : "text-xl md:text-2xl",
              )}
            >
              {label}
            </h3>
          </div>
        </div>

        <p className="mt-5 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_60%,transparent)] bg-toot-surface-raised/65 px-3 py-2 text-sm font-semibold leading-6 tracking-[-0.01em] text-[color-mix(in_oklab,var(--color-toot-foreground)_90%,transparent)] [overflow-wrap:anywhere]">
          {value}
        </p>

        <p className="mt-4 mb-4 max-w-[58ch] text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_76%,transparent)] sm:text-[0.95rem] sm:leading-7">
          {description}
        </p>

        {href && actionLabel ? (
          <span className="mt-auto inline-flex min-h-11 w-fit items-center justify-center gap-2 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_54%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_11%,transparent)] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[var(--color-toot-orange)] transition-[background-color,border-color,color,transform] duration-200 ease-out group-hover/contact:-translate-y-0.5 group-hover/contact:border-[var(--color-toot-red)] group-hover/contact:bg-[var(--color-toot-red)] group-hover/contact:text-[var(--color-primary-foreground)] motion-reduce:transition-none motion-reduce:group-hover/contact:translate-y-0">
            <span>{actionLabel}</span>
            <span aria-hidden="true">→</span>
          </span>
        ) : null}
      </div>
    </>
  );

  const classes = cn(
    "group/contact relative isolate flex h-full min-w-0 flex-col overflow-hidden rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_78%,transparent)] bg-[radial-gradient(circle_at_100%_0%,rgba(255,77,46,0.16),transparent_15rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-surface-terminal)_72%,var(--color-toot-black))] text-[var(--color-toot-foreground)] shadow-[0_1.25rem_3.5rem_rgba(0,0,0,0.34)]",
    href &&
      "transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-[color-mix(in_oklab,var(--color-toot-orange)_58%,var(--color-toot-grid-strong))] hover:shadow-[0_1.5rem_4.5rem_rgba(0,0,0,0.46),0_0_0_1px_rgba(255,77,46,0.13)] focus-visible:border-[var(--color-toot-orange)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
    isPrimary &&
      "bg-[radial-gradient(circle_at_100%_0%,rgba(255,77,46,0.22),transparent_16rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-surface-terminal)_70%,var(--color-toot-black))]",
    className,
  );

  return href ? (
    <Link
      aria-label={ariaLabel ?? label}
      className={classes}
      href={href}
      target={target ?? undefined}
    >
      {body}
    </Link>
  ) : (
    <article className={classes}>{body}</article>
  );
};

TootContactCard.displayName = "Contact::TootContactCard";
